import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useUpdateConnector } from "@/features/connectors/hooks/use-connectors";
import type { Connector } from "@/features/connectors/types";
import { useTranslation } from "@/features/utils/i18n";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ConnectorForm, type ConnectorFormData } from "./connector-form";
import {
  emailAccountPayload,
  sipRegistrationPayload,
} from "./connector-form/types";
import { MetaEditDialog } from "./connect-meta/meta-edit-dialog";

interface EditConnectorDialogProps {
  connector: Connector;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EditConnectorDialog = ({
  connector,
  open,
  onOpenChange,
}: EditConnectorDialogProps) => {
  const { t } = useTranslation("connectors");
  const { company: clientId } = useParams<{ company: string }>();

  const updateConnector = useUpdateConnector();

  // A connected Meta account has no credentials or numbers to edit: its own,
  // much smaller, dialog.
  if (connector.type === "meta") {
    return (
      <MetaEditDialog
        connector={connector}
        open={open}
        onOpenChange={onOpenChange}
      />
    );
  }

  const handleUpdate = async (formData: ConnectorFormData) => {
    if (!clientId) {
      toast.error(t("connectors.messages.noClientId"));
      return;
    }

    const phoneNumbersArray = formData.phoneNumbers
      .split(",")
      .map((num) => num.trim())
      .filter(Boolean);

    const whatsappNumbersArray = formData.whatsappNumbers
      .split(",")
      .map((num) => num.trim())
      .filter(Boolean);

    const updateData: {
      status: typeof formData.status;
      capabilities: typeof formData.capabilities;
      agent_id?: string;
      configuration: {
        account_id: string;
        twilio_region: string;
        phone_numbers: string[];
        whatsapp_numbers: string[];
        auth_token?: string;
        routing_rules: typeof formData.routingRules;
        geo_filter: typeof formData.geoFilter;
        outbound_filter: typeof formData.outboundFilter;
        fallback_destinations?: typeof formData.fallbackDestinations;
        agent_off_schedule_id: string;
        ring_before_agent_seconds: number;
        fallback_destination?: undefined;
        fallback_member_id?: string;
        fallback_number?: string;
        allowed_origins?: string[];
        caller_numbers?: string[];
        demo_number?: boolean;
        code_routing?: boolean;
        default_connector?: boolean;
        web_call_enabled?: boolean;
        public_callback_enabled?: boolean;
        sip_registration?: ReturnType<typeof sipRegistrationPayload>;
        email_account?: ReturnType<typeof emailAccountPayload>;
      };
    } = {
      status: formData.status,
      capabilities: formData.capabilities,
      agent_id: formData.agentId || undefined,
      configuration: {
        account_id: formData.accountId,
        twilio_region: formData.twilioRegion,
        phone_numbers: phoneNumbersArray,
        whatsapp_numbers: whatsappNumbersArray,
        routing_rules: formData.routingRules,
        geo_filter: formData.geoFilter,
        outbound_filter: formData.outboundFilter,
        fallback_destinations: formData.fallbackDestinations,
        // "" rather than absent: an absent key keeps the stored value, and
        // unticking the box has to switch the window off.
        agent_off_schedule_id: formData.agentOffScheduleId,
        // 0 rather than absent, for the same reason: clearing the field has to
        // switch the hand-over off.
        ring_before_agent_seconds: formData.ringBeforeAgentSeconds,
        // Cleared on save: the ordered list above is the only source of truth
        // once a connector has been edited.
        fallback_destination: undefined,
        fallback_member_id: undefined,
        fallback_number: undefined,
        allowed_origins: formData.allowedOrigins
          .split(/[,\n]/)
          .map((o) => o.trim())
          .filter(Boolean),
        caller_numbers: formData.callerNumbers
          .split(",")
          .map((num) => num.trim())
          .filter(Boolean),
        demo_number: formData.demoNumber,
        code_routing: formData.codeRouting,
        default_connector: formData.defaultConnector,
        web_call_enabled: formData.webCallEnabled,
        public_callback_enabled: formData.publicCallbackEnabled,
        sip_registration: sipRegistrationPayload(formData),
        email_account: emailAccountPayload(formData),
      },
    };

    // Only include auth_token if it was changed
    if (formData.authToken) {
      updateData.configuration.auth_token = formData.authToken;
    }

    try {
      await updateConnector.mutateAsync({
        clientId,
        connectorId: connector.id!,
        data: updateData,
      });

      toast.success(t("connectors.messages.updated"));
      onOpenChange(false);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("connectors.messages.updateError"),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("connectors.edit.title")}</DialogTitle>
          <DialogDescription>
            {t("connectors.edit.description")}
          </DialogDescription>
        </DialogHeader>

        <ConnectorForm
          mode="edit"
          connector={connector}
          onSubmit={handleUpdate}
          isSubmitting={updateConnector.isPending}
        />
      </DialogContent>
    </Dialog>
  );
};
